/**
 * ============================================================================
 * ⭐⭐ A5 — THE REPLACE POPUP (2026-09-28)
 * ============================================================================
 *
 * > Pete, 2026-09-27: *"we can't say this order is on multiple appointments,
 * > show the appointments its on and then have a checkbox next to each and say
 * > replace"*.
 *
 * Shown when an order dropped onto an appointment is already attached to one or
 * more OTHER appointments (PLAN-wms-panel-match-attach §5).
 *
 * ⛔⛔ THE DEFAULT IS NOTHING TICKED. Attaching adds; removing is a second,
 * deliberate act. A dialog that arrives with destructive boxes pre-ticked is a
 * dialog people dismiss without reading.
 *
 * ⛔ ONLY LINK ROWS ARE LISTED. An appointment that matches the order on its PO
 * or container keeps matching it whatever is ticked here, so offering it a box
 * would promise a removal nothing can perform. The server returns link rows
 * only; this component does not add any.
 *
 * ⚠️ LINKS THE SCHEDULER CANNOT SEE ARE COUNTED, NOT LISTED (`hiddenElsewhere`).
 * Saying "and 1 more at a site you cannot see" is truer than a list that looks
 * complete.
 */
import { useEffect, useState } from 'react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import type { WmsOrderLinkDto } from '@/api/client';
import { appointmentTimeLabel, hhmm } from '@/lib/utils';

export interface WmsOrderReplaceDialogProps {
    open: boolean;
    orderNumber: string;
    /** The appointment the order was dropped on. */
    targetAppointmentNumber: string;
    /** OTHER appointments this order is linked to - never the target. */
    links: WmsOrderLinkDto[];
    hiddenElsewhere: number;
    busy?: boolean;
    /** The ids a person ticked. Empty means attach and remove nothing. */
    onConfirm: (removeFrom: number[]) => void;
    onCancel: () => void;
}

function whenOf(l: WmsOrderLinkDto): string {
    const day = l.scheduledStartUtc ? new Date(l.scheduledStartUtc) : null;
    const dayText = day && !Number.isNaN(day.getTime())
        ? day.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })
        : '';
    /* ⛔ `appointmentTimeLabel` decides between a clock and 'All day' - a day-only
       booking's stored start is the site's opening time, not a promise. */
    const clock = appointmentTimeLabel(l, hhmm(l.scheduledStartUtc));
    return [l.dockDoorCode, clock, dayText].filter(Boolean).join(' · ');
}

export function WmsOrderReplaceDialog({
    open, orderNumber, targetAppointmentNumber, links, hiddenElsewhere, busy = false, onConfirm, onCancel,
}: WmsOrderReplaceDialogProps) {
    const [ticked, setTicked] = useState<Set<number>>(new Set());

    /* ⛔ CLEARED EVERY TIME IT OPENS. A tick left over from the last drop would be
       a removal nobody chose for this one. */
    useEffect(() => { if (open) setTicked(new Set()); }, [open]);

    const total = links.length + hiddenElsewhere;
    const toggle = (id: number) => setTicked(prev => {
        const n = new Set(prev);
        if (n.has(id)) n.delete(id); else n.add(id);
        return n;
    });

    return (
        <Dialog open={open} onOpenChange={(o) => !o && !busy && onCancel()}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>
                        {orderNumber} is already attached to {total} appointment{total === 1 ? '' : 's'}
                    </DialogTitle>
                    <DialogDescription>
                        Tick any it should come OFF. {targetAppointmentNumber} gets the order either way.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-1">
                    {links.map(l => (
                        <label key={l.appointmentId}
                               className="flex cursor-pointer items-center gap-2 rounded border px-2 py-1.5 text-sm hover:bg-muted/50">
                            <input
                                type="checkbox"
                                checked={ticked.has(l.appointmentId)}
                                onChange={() => toggle(l.appointmentId)}
                                disabled={busy}
                            />
                            <span className="font-medium">{l.appointmentNumber}</span>
                            <span className="text-muted-foreground">{whenOf(l)}</span>
                        </label>
                    ))}
                    {hiddenElsewhere > 0 && (
                        <p className="px-2 text-xs text-muted-foreground">
                            …and {hiddenElsewhere} more at {hiddenElsewhere === 1 ? 'a site' : 'sites'} you cannot see. {hiddenElsewhere === 1 ? 'It stays' : 'They stay'} attached.
                        </p>
                    )}
                </div>

                <p className="text-xs text-muted-foreground">
                    {ticked.size === 0
                        ? 'Nothing ticked: the order is added, and nothing is removed.'
                        : `Ticked links will be REMOVED (${ticked.size}).`}
                </p>

                <DialogFooter>
                    <div className="flex w-full justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={onCancel} disabled={busy}>Cancel</Button>
                        <Button
                            variant={ticked.size > 0 ? 'destructive' : 'default'}
                            size="sm"
                            onClick={() => onConfirm([...ticked])}
                            disabled={busy}
                        >
                            {ticked.size > 0 ? `Attach and remove ${ticked.size}` : 'Attach'}
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
